import moment from "moment";
import "moment/locale/ko";
import axios from "axios";
import uuid from "react-uuid";

// 작성자 동일여부 확인
export const isSameUser = (user, writer) => {
  if (!user || !writer || !user.mberNo || !writer.mberNo) return false;
  return user.mberNo.toString() === writer.mberNo.toString();
};

// 하루종일 여부 확인
export const isEventAllTime = (item) => {
  const { startDt, endDt } = item;
  if (!startDt || !endDt) return false;

  const [dateStart, timeStart] = startDt.split(" ");
  const [dateEnd, timeEnd] = endDt.split(" ");

  return timeStart === "00:00:00" && timeEnd === "23:59:59";
};

// 색상 추출
export const hexToRgb = (hex) => {
  if (!hex) return "";

  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `${r}, ${g}, ${b}`;
};

// 외부업체 체크
export const isOutsideCmpny = (item, company) => {
  if (!item || !company) return false;
  if (item.gbn !== "C") {
    return false;
  }
  if (company.some((cmpny) => cmpny.cmpnyNo === item.orgNo)) {
    return false;
  }
  return true;
};

// 날씨 표기
export const isWeather = (weather, showTemperature = false) => {
  if (weather === null || weather === undefined) return <></>;
  const state = weatherStates[weather.weather];
  if (!state) return <></>;

  const iconChar = String.fromCodePoint(parseInt(state.icon, 16));

  return (
    <div className="weather">
      <span className="icon" style={{ color: state.color }}>
        {iconChar}
      </span>
      {showTemperature && (
        <span>
          {weather.temMin}℃/{weather.temMax}℃
        </span>
      )}
    </div>
  );
};

// 특정 날짜를 기준으로 날짜 범위 설정
export const getDateRange = (baseDate = moment()) => {
  const month = moment(baseDate).startOf("month");
  return {
    startDt: month.subtract(10, "days").format("YYYY-MM-DD"),
    endDt: month.add(41, "days").format("YYYY-MM-DD"),
  };
};

// 업무 및 그룹 선택한 것만 필터링
export const filterOrg = (org) => {
  if (!org) return [];
  return org.filter((item) => item.check).map((item) => item.orgNo);
};

// 일정 유형별 색
const getColorByGbn = (gbn) => {
  switch (gbn) {
    case "P":
      return "139859";
    case "C":
      return "0074ca";
    case "G":
      return "f06f02";
    case "O":
      return "ED5583";
    default:
      return "cccccc";
  }
};

// 이벤트 그룹화 (유형별)
export const tallyEvents = (data = []) => {
  const result = [];

  data.forEach((item) => {
    const { startDt, endDt, gbn } = item;

    if (item.dday !== "Y" && gbn !== "H") {
      const startDate = startDt.split(" ")[0];
      const endDate = endDt.split(" ")[0];

      const duration = moment(endDate).diff(moment(startDate), "days");

      for (let i = 0; i <= duration; i++) {
        const current = moment(startDate).add(i, "days").format("YYYY-MM-DD");
        const existingEvent = result.some((event) => event.start === current && event.gbn === gbn);

        if (existingEvent) {
          const eventIndex = result.findIndex(
            (event) => event.start === current && event.gbn === gbn,
          );
          result[eventIndex].title++;
        } else {
          const color = `rgba(${hexToRgb(getColorByGbn(gbn))}, 0.8)`;
          result.push({
            start: current,
            gbn: gbn,
            title: 1,
            backgroundColor: color,
            borderColor: color,
          });
        }
      }
    }
  });

  return result;
};

// FullCalendar 형식 맞추기
export const transformEvents = async (origin = [], company) => {
  const events = [];
  const holidays = [];
  const isProject = location.pathname.includes("/mypage/project/");

  origin.forEach((item) => {
    const start = moment(item.startDt);
    const end = adjustEndDate(item.endDt);
    const isHoliday = ["S", "H"].includes(item.gbn);
    const isDday = item.dday === "Y";
    const isAllTimeEvent =
      isEventAllTime(item) ||
      isHoliday ||
      isDday ||
      start.format("YYYY-MM-DD") !== moment(item.endDt).format("YYYY-MM-DD");

    const baseColor = isDday
      ? "#333333"
      : isProject && item.createDt !== item.updateDt
      ? "#da0000"
      : getEventColor(item, company, getOpacity(item.completeYn));

    const event = {
      ...item,
      id: item.scheduleNo,
      start: isHoliday ? start.format("YYYY-MM-DD 23:59:58") : item.startDt,
      end: end.format("YYYY-MM-DD HH:mm:ss"),
      backgroundColor: baseColor,
      borderColor: baseColor,
      allDay: false,
      display: isAllTimeEvent ? "block" : "list-item",
    };

    (isHoliday ? holidays : events).push(event);
  });

  return { events, holidays };
};

// 종료 시간 조정
const adjustEndDate = (endDt) => {
  let end = moment(endDt);
  if (end.format("HH:mm:ss") === "00:00:00") {
    end = end.add(1, "seconds");
  }
  return end;
};

// 완료 및 미완료일정 투명도
const getOpacity = (completeYn) => (completeYn === "Y" ? 0.5 : 0.8);

// 일정별 색상
export const getEventColor = (item, company, opacity) => {
  const gbnColor = {
    P: item.color,
    C: isOutsideCmpny(item, company) ? "ed5583" : "0074CA",
    G: "F46F02",
    H: "D42625",
    S: "999999",
  };

  const selectedColor = gbnColor[item.gbn] || "000000";
  return `rgba(${hexToRgb(selectedColor)}, ${opacity})`;
};

// 클래스명과 일치하는 데이터 찾기
export const getIdFromClass = (data, item) => {
  const match = item.className.match(/schedule-(\d+)/);

  let id;
  if (match) {
    id = parseInt(match[1]);

    const event = data.find((item) => item.scheduleNo === id);
    return event;
  }

  return null;
};

// 날짜가 범위의 조회 날짜와 다른지
export const isMonthDifferent = (onDate, range) => {
  const { startDt, endDt } = range;

  const onDateMonth = moment(onDate).format("YYYY-MM");

  const diffInDays = moment(endDt).diff(startDt, "days");
  const middle = moment(startDt)
    .add(Math.floor(diffInDays / 2), "days")
    .format("YYYY-MM");

  return middle !== onDateMonth;
};

// 컬러값에 따른 클래스명 지정
const assignColorClass = (hex) => {
  if (!hex || typeof hex !== "string") {
    return "EM";
  }

  const color = hex.toUpperCase();

  switch (color) {
    case "0074CA":
      return "C";
    case "F46F02":
      return "G";
    case "ED5583":
      return "O";
    case "FFC455":
      return "GS";
    case "8F2D56":
      return "BB";
    case "A7A3D9":
      return "LL";
    case "38C6BD":
      return "TT";
    case "B350C3":
      return "VL";
    default:
      return "EM";
  }
};

// 날짜별 일정 개수
export const createDailyEvent = (data = []) => {
  const newEvents = [];
  const eventsCount = {};

  data.forEach((item) => {
    if (item.dday === "Y") {
      return;
    }

    const { startDt, endDt, color, id, title } = item;
    const [startDate] = startDt.split(" ");
    const [endDate] = endDt.split(" ");

    const duration = moment(endDate).diff(moment(startDate), "days");
    for (let i = 0; i <= duration; i++) {
      const current = moment(startDate).add(i, "days").format("YYYY-MM-DD");
      if (!eventsCount[current] || eventsCount[current] < 3) {
        const className = `schedule${assignColorClass(color)}`;
        newEvents.push({
          title,
          id,
          start: current,
          className,
        });
        eventsCount[current] = (eventsCount[current] || 0) + 1;
      }
    }
  });

  return newEvents;
};

// 선택된 날짜, 시작/종료 날짜 비교
export const timeByDate = (selected, startDt, endDt) => {
  const select = moment(selected);
  const start = moment(startDt);
  const end = moment(endDt);

  const formatTime = (date) => date.format("LT");
  const isFullDay =
    start.format("HH:mm:ss") === "00:00:00" && end.format("HH:mm:ss") === "23:59:59";
  const isSelectedStart = select.isSame(start, "day");
  const isSelectedEnd = select.isSame(end, "day");

  if (isFullDay) {
    return "하루종일";
  }
  if (isSelectedStart && isSelectedEnd) {
    return `${formatTime(start)} ~ ${formatTime(end)}`;
  }
  if (isSelectedStart) {
    return `${formatTime(start)} 시작`;
  }
  if (isSelectedEnd) {
    return `${formatTime(end)} 종료`;
  }
  if (select.isAfter(start) && select.isBefore(end)) {
    return "하루종일";
  }
  return "";
};

// 시작 날짜, 종료 날짜 출력
export const showEventTime = (startDt, endDt) => {
  const start = moment(startDt);
  const end = moment(endDt);

  const startDate = start.format("M월 D일 dddd");
  const endDate = end.format("M월 D일 dddd");
  const startTime = start.format("a h:mm");
  const endTime = end.format("a h:mm");

  const isStart = start.format("HH:mm:ss") === "00:00:00";
  const isEnd = end.format("HH:mm:ss") === "23:59:59";
  const isAllDayEvent = isStart && isEnd;

  if (isAllDayEvent) {
    if (startDate === endDate) {
      return `${startDate} 하루종일`;
    } else {
      return `${startDate} ~ ${endDate} 하루종일`;
    }
  } else {
    if (startDate === endDate) {
      return `${startDate} ${startTime} ~ ${endTime}`;
    } else {
      return `${startDate} ${startTime} ~ ${endDate} ${endTime}`;
    }
  }
};

// 일정 분류
export const isEventCategory = (gbn) => {
  const categories = {
    P: "개인",
    C: "업무",
    G: "모임",
    O: "외부",
  };
  return categories[gbn];
};

// 특정 날짜에 해당하는 데이터 찾기
export const isSameDay = (target, start, end) => {
  if (end) {
    return (
      target.isSame(start, "day") ||
      target.isSame(end, "day") ||
      (target.isAfter(start) && target.isBefore(end))
    );
  } else {
    return target.isSame(start, "day");
  }
};

// 정렬 기준 - 디데이, 시작시간, 지속기간순
export const eventsSorted = (events) => {
  return events.sort((a, b) => {
    if (a.dday !== b.dday) {
      return a.dday === "Y" ? -1 : 1;
    }

    const startA = moment(a.startDt);
    const startB = moment(b.startDt);

    if (startA.isSame(startB)) {
      const durationA = a.endDt ? moment(a.endDt).diff(startA) : 0;
      const durationB = b.endDt ? moment(b.endDt).diff(startB) : 0;
      return durationB - durationA;
    }

    return startA.diff(startB);
  });
};

// 디데이 계산
export const calculateDday = (eventDate) => {
  const today = moment().startOf("day");
  const startEvent = moment(eventDate).startOf("day");
  const diffDays = startEvent.diff(today, "days");

  if (diffDays > 0) {
    return `D-${diffDays}`;
  } else if (diffDays < 0) {
    return `D+${Math.abs(diffDays)}`;
  } else {
    return "D-day";
  }
};

// 날짜 형식 변환
export const transformDate = (date, isAll) => {
  if (!date) return;

  const { dateStart, dateEnd, timeStartHour, timeStartMinute, timeEndHour, timeEndMinute } = date;

  let startDt = `${dateStart} ${timeStartHour}:${timeStartMinute}:00`;
  let endDt = `${dateEnd} ${timeEndHour}:${timeEndMinute}:00`;

  if (isAll) {
    startDt = `${dateStart} 00:00:00`;
    endDt = `${dateEnd} 23:59:59`;
  }

  return { startDt, endDt };
};

// 불필요한 데이터 지우기
export const keysToRemove = (item, deletionKeys = []) => {
  let keys = ["backgroundColor", "borderColor", "start", "end", "id", "display"];

  if (deletionKeys.length > 0) {
    keys = keys.concat(deletionKeys);
  }

  const newItem = { ...item };
  keys.forEach((key) => {
    if (newItem.hasOwnProperty(key)) {
      delete newItem[key];
    }
  });
  return newItem;
};

// 날짜 형식 맞추기
export const setDateObject = (dateStart, dateEnd, timeStart, timeEnd) => {
  const [startHour, startMinute] = timeStart.split(":");
  const [endHour, endMinute] = timeEnd.split(":");

  return {
    dateStart,
    dateEnd,
    timeStartHour: startHour,
    timeStartMinute: startMinute,
    timeEndHour: endHour,
    timeEndMinute: endMinute,
  };
};

// 5분 단위 확인, 아닐 경우 반올림
export const roundTime = (time) => {
  if (parseInt(time.split(":")[1]) % 5) {
    const [hours, minutes, seconds] = time.split(":");
    const roundedMinutes = Math.round(parseInt(minutes) / 5) * 5;
    return `${hours}:${roundedMinutes < 10 ? "0" : ""}${roundedMinutes}:${seconds}`;
  } else {
    return time;
  }
};

// 프로젝트 참여자 조회
export const getProjectMbers = async (projectNo, user) => {
  const url = "/api/participantList";
  const body = {
    projectNo,
    delYn: "N",
    offset: 0,
    pageNumber: 0,
    pageSize: 10,
    paged: false,
  };

  const res = await axios.post(url, body);
  if (res.status === 200) {
    const list = res.data.content;

    if (!list.find((item) => item.mberNo === user.mberNo)) {
      return false;
    } else {
      return true;
    }
  }
};

// 투두 양식
export const updateTodos = (todos, memo) => {
  const memoParts = (memo || "").split("\n");

  return Array.from({ length: todos.length + memoParts.length }, (_, index) => {
    const existingTodo = todos.find((todo) => todo.index === index);

    if (existingTodo) {
      return existingTodo;
    } else if (memoParts.length > 0) {
      return {
        id: null,
        content: memoParts.shift(),
        index,
        isCompleted: false,
        isDeleted: false,
        mentions: [],
        updatedAt: null,
      };
    }
    return null;
  }).filter(Boolean);
};

// 투두 공백 제거
export const transformTodoContent = (text) => {
  if (!text) return "";
  return text.replace(/^[\uffff￿\s]+/, "") || "";
};

// 투두 양식
export const transformTodo = (todos) => {
  const data = todos.map((todo) => ({
    id: todo.id,
    value: todo.content?.replace(/^[\uffff￿\s]+/, "") || "",
    mentionUserIds:
      todo.mentions.length > 0 ? todo.mentions.map((mention) => mention.mberNo.toString()) : [],
    showBullet: todo.id ? true : false,
    isChecked: todo.isCompleted,
  }));

  return data;
};

const weatherStates = {
  0: { text: "맑음", icon: "2600", color: "#dab202" },
  1: { text: "구름많음", icon: "2601", color: "#0d5781" },
  2: { text: "흐림", icon: "2601", color: "#173747" },
  3: { text: "비", icon: "1F327", color: "#028bda" },
  4: { text: "비/눈", icon: "1F328", color: "#028bda" },
  5: { text: "눈", icon: "2744", color: "#243ab6" },
  6: { text: "소나기", icon: "2614", color: "#8e03aa" },
};

export const icons = [
  { src: "artist_palette_3d.png", name: "팔레트" },
  { src: "badminton_3d.png", name: "배드민턴" },
  { src: "bank_3d.png", name: "은행" },
  { src: "baseball_3d.png", name: "야구" },
  { src: "basketball_3d.png", name: "농구" },
  { src: "beer_mug_3d.png", name: "맥주잔" },
  { src: "birthday_cake_3d.png", name: "케이크" },
  { src: "bottle_with_popping_cork_3d.png", name: "병" },
  { src: "bouquet_3d.png", name: "꽃다발" },
  { src: "bowling_3d.png", name: "볼링" },
  { src: "clinking_beer_mugs_3d.png", name: "맥주" },
  { src: "credit_card_3d.png", name: "카드" },
  { src: "flag_in_hole_3d 1.png", name: "골프" },
  { src: "fork_and_knife_with_plate_3d.png", name: "접시" },
  { src: "guide_dog_3d.png", name: "강아지" },
  { src: "hospital_3d.png", name: "병원" },
  { src: "hot_beverage_3d.png", name: "커피" },
  { src: "monorail_3d.png", name: "기차" },
  { src: "newspaper_3d.png", name: "신문" },
  { src: "open_book_3d.png", name: "책" },
  { src: "pill_3d.png", name: "알약" },
  { src: "ping_pong_3d.png", name: "탁구" },
  { src: "popcorn_3d.png", name: "팝콘" },
  { src: "shopping_cart_3d.png", name: "쇼핑 카트" },
  { src: "soccer_ball_3d.png", name: "축구공" },
  { src: "teacup_without_handle_3d.png", name: "찻잔" },
  { src: "woman_with_veil_3d_light.png", name: "여성" },
  { src: "writing_hand_3d_light.png", name: "글" },
];

export const transformScheduleData = (schedules) => {
  return schedules.reduce((acc, item) => {
    const startDate = moment(item.startDt).startOf("day");
    const endDate = moment(item.endDt).startOf("day");

    for (let date = startDate; date.isSameOrBefore(endDate, "day"); date.add(1, "days")) {
      const year = date.year();
      const month = date.month() + 1;
      const day = date.date();
      if (!acc[year]) acc[year] = {};
      if (!acc[year][month]) acc[year][month] = {};
      if (!acc[year][month][day]) acc[year][month][day] = [];
      acc[year][month][day].push(item);
    }

    return acc;
  }, {});
};
